import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Screen, Chip, ChipRow } from "@/components/kit";
import { useSession } from "@/context/SessionContext";
import { useT } from "@/lib/i18n";
import { UST_DATABASE, calculateGpi, endsLabel, formatGal, formatGpi, tankKey } from "@/lib/tank";

const FILTERS = [["all", "All", () => true], ["small", "Under 5k", (c) => c < 5000], ["mid", "5k–15k", (c) => c >= 5000 && c <= 15000], ["large", "15k+", (c) => c > 15000]];

export default function TanksPage() {
  const { session: s, applyTank } = useSession();
  const nav = useNavigate();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const t = useT();
  const words = q.toLowerCase().replace(/,/g, "").split(/\s+/).filter(Boolean);
  const text = (x) => `${x.capacity} ${x.diameter} ${x.mfg} ${t(x.mfg)} ${x.lengthLabel} ${t(endsLabel(x.ends))}`.toLowerCase();
  const inRange = FILTERS.find((f) => f[0] === filter)[2];
  const list = UST_DATABASE.filter((x) => inRange(x.capacity) && words.every((w) => text(x).includes(w)));
  const current = tankKey(s);

  return (
    <Screen testID="tanks-screen" title={t("Tank reference")} subtitle={t("{n} standard UST sizes, small to large", { n: list.length })}>
      <label className="tank-search" data-testid="tank-search">
        <Search size={16} />
        <input className="text-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search size or maker, e.g. 10000 or Xerxes")} data-testid="tank-search-input" />
      </label>
      <ChipRow testID="tank-filter-row">
        {FILTERS.map(([id, label]) => (
          <Chip key={id} testID={`tank-filter-${id}`} active={filter === id} onPress={() => setFilter(id)}>{t(label)}</Chip>
        ))}
      </ChipRow>
      {!list.length && <div className="empty-result" data-testid="tank-search-empty">{t("No standard tank matches that. Try just the gallons, like 10000.")}</div>}
      <div className="tanks-list" data-testid="tanks-list">
        {list.map((x) => {
          const key = tankKey(x), active = key === current;
          return (
            <div key={key} className={`tank-row ${active ? "tank-row-active" : ""} ${x.sti ? "tank-row-sti" : ""}`} data-testid={`tank-row-${key}`}>
              <div className="tank-main">
                <div className="top-line">
                  <span className="cap">{formatGal(x.capacity)} gal</span>
                  <span className={`dia ${x.sti ? "sti-text" : ""}`}>{t("{d}\" wide", { d: x.diameter })}</span>
                </div>
                <div className="meta">{t(endsLabel(x.ends))} · {t("{g} gpi center", { g: formatGpi(calculateGpi(x.diameter / 2, x.diameter, x.capacity, x.ends)) })}</div>
                <div className="mfg">{t(x.ends === "dome" ? "Fiberglass" : "Steel")} · {x.lengthLabel}{x.sti ? ` · ${t("12 ft stick")}` : ""}</div>
              </div>
              <button type="button" className={`load-btn ${active ? "load-btn-active" : ""}`} data-testid={`load-tank-${key}`}
                onClick={() => { applyTank(x.capacity, x.diameter, x.ends); nav("/drop"); }}>
                {t(active ? "In use" : "Use this tank")}
              </button>
            </div>
          );
        })}
      </div>
    </Screen>
  );
}
